import { useQuery } from '@tanstack/react-query';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { clients } from '@/shared/utils/graphql';
import {
  archiveInfoQuery,
  getBlockCountsQuery,
  getBlocksEventsAndExtrinsicsCount,
} from '../graphql/explorer/blocks';

export type BlockCounts = { extrinsicsCount: number; eventsCount: number } | 'unavailable';

const ARCHIVE_INFO_STALE_MS = 5 * 60_000;

const isUnknownFieldError = (error: unknown) => {
  const errors = (error as { response?: { errors?: { message?: string }[] } })?.response?.errors;
  return errors?.some((e) => e.message?.includes('archiveInfo')) ?? false;
};

/**
 * A gateway that predates the S3 archive has neither `archiveInfo` nor `getBlockCounts`, and
 * rejects the whole document at validation rather than answering in part. Asking for
 * `archiveInfo` therefore doubles as the capability probe.
 *
 * Only that specific rejection is swallowed. A network failure is rethrown, both so it
 * retries and so it still reaches the query cache's Sentry reporter — and so a healthy new
 * gateway is never mistaken for an old one and sent down the fetch-everything-to-count-it path.
 */
const useArchiveInfo = () =>
  useQuery({
    queryKey: ['archiveInfo'],
    staleTime: ARCHIVE_INFO_STALE_MS,
    queryFn: async () => {
      try {
        const { archiveInfo } = await clients.processor.request({ document: archiveInfoQuery });
        return { supported: true, lastArchivedBlock: archiveInfo.lastArchivedBlock ?? null };
      } catch (error) {
        if (isUnknownFieldError(error)) return { supported: false, lastArchivedBlock: null };
        throw error;
      }
    },
  });

/**
 * Per-height extrinsic and event counts for a range of blocks.
 *
 * Blocks at or below `archiveInfo.lastArchivedBlock` have been moved to S3, where counting
 * them means scanning a whole partition to produce two numbers — so the gateway does not
 * serve counts for them at all. Only the unarchived part of the range is requested; archived
 * heights come back as `'unavailable'` for the caller to render as n/a.
 */
const useBlockCounts = (startBlockHeight?: number, endBlockHeight?: number) => {
  const { data: archive } = useArchiveInfo();

  const lastArchivedBlock = archive?.lastArchivedBlock ?? null;
  const legacyGateway = archive?.supported === false;
  const known = archive != null && startBlockHeight != null && endBlockHeight != null;

  const firstUnarchived = lastArchivedBlock == null ? startBlockHeight : lastArchivedBlock + 1;
  const range =
    known && firstUnarchived != null && endBlockHeight >= firstUnarchived
      ? { startBlockHeight: Math.max(startBlockHeight, firstUnarchived), endBlockHeight }
      : undefined;

  const { data: counts } = useGqlQuery(getBlockCountsQuery, {
    variables: range,
    enabled: range != null && !legacyGateway,
  });
  const { data: legacy } = useGqlQuery(getBlocksEventsAndExtrinsicsCount, {
    variables: range,
    enabled: range != null && legacyGateway,
  });

  // A disabled query still resolves to whatever its cache key last held, so the range — not
  // the presence of data — decides what belongs to this page.
  let rows: { blockHeight: number; extrinsicsCount: number; eventsCount: number }[] = [];
  if (range && legacyGateway) rows = legacy?.getBlocksDetails.blocks ?? [];
  else if (range) rows = counts?.getBlockCounts ?? [];

  const byHeight = new Map(rows.map((row) => [row.blockHeight, row]));

  return (blockHeight: number): BlockCounts | undefined => {
    if (lastArchivedBlock != null && blockHeight <= lastArchivedBlock) return 'unavailable';
    const row = byHeight.get(blockHeight);
    return row && { extrinsicsCount: row.extrinsicsCount, eventsCount: row.eventsCount };
  };
};

export default useBlockCounts;
